import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/cjc/components/chart/Chart'

import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { renderImportanceTableProps } from '@aytm/app/stats/page/question-views/cjc/lib/table-utils'

// components
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import Chart from '@aytm/app/stats/page/question-views/cjc/components/chart/Chart'
import LiteChart from '@aytm/app/stats/page/question-views/cjc/components/lite-chart/LiteChart'
import Items from '@aytm/app/stats/page/question-views/cjc/components/items/Items'
import MarketSimulator from '@aytm/app/stats/page/question-views/cjc/components/market-simulator/MarketSimulator'

// Rich-chart export dropdown is always shown (legacy behavior) -- its own image
// export is not gated by the page-level export feature flag, unlike the lite chart.
const RICH_CHART_FEATURES = {}

// What both conjoint blocks draw once their chart is worth drawing: the rich D3 chart or the
// importance-% lite chart, then the package batches and the market simulator. The chart is
// scroll-lazy via ChartPlaceholder (the React equivalent of the old block_base renderChart +
// $onVisible path).
const Body = ({
  answer,
  persona,
  questionType,
  batches,
  features,
  mode,
  hiddenBatches,
  currentPackage,
  packageRankSharesArr,
  packageRankScoresArr,
  sortOrder,
  colorTheme,
  chartLegendMode,
  questionId,
  questionKey,
  chartMode,
  questionChartMode,
  globalChartMode,
  zoomLevel,
  globalZoomLevel,
  isSummaryView,
  isAnimated,
  scale,
  center,
  components,
  uncollapsed,
  hideTotal,
  processText,
  marketSimulatorPackages,
  marketSimulatorNA,
  onPackageChange,
  onHiddenBatchesChange,
  onChartExport,
  onChartReady,
  onChartModeChange,
  onZoomChange,
  onModeChange,
  onSortChange,
  onToggleAll,
  onToggleCollapse,
  onBatchPackageChange,
  onNAChange,
  onMarketSimulatorExport,
  addMarketSimulatorPackage,
  deleteMarketSimulatorPackage,
  setMarketSimulatorPackage
}) => (
  <>
    {isComponentOn(components, 'chart') && !isNoneChartMode(questionChartMode, globalChartMode) && (
      <ChartPlaceholder height={CHART_HEIGHT} required isLoading={'loading' === answer} isAnimated={isAnimated}>
        {isSummaryView ? (
          <LiteChart
            questionId={questionId}
            questionKey={questionKey}
            qType={questionType}
            answer={answer}
            batches={batches}
            persona={persona}
            sortOrder={sortOrder}
            chartMode={chartMode}
            questionChartMode={questionChartMode}
            globalChartMode={globalChartMode}
            colorTheme={colorTheme}
            chartLegendMode={chartLegendMode}
            features={features}
            onChartModeChange={onChartModeChange}
            zoomLevel={zoomLevel}
            globalZoomLevel={globalZoomLevel}
            onZoomChange={onZoomChange}
          />
        ) : (
          <Chart
            questionId={questionId}
            answer={answer}
            batches={batches}
            features={RICH_CHART_FEATURES}
            mode={mode}
            persona={persona}
            hiddenBatches={hiddenBatches}
            currentPackage={currentPackage}
            packageRankSharesArr={packageRankSharesArr}
            packageRankScoresArr={packageRankScoresArr}
            scale={scale}
            center={center}
            sortOrder={sortOrder}
            colorTheme={colorTheme}
            chartLegendMode={chartLegendMode}
            onPackageChange={onPackageChange}
            onHiddenBatchesChange={onHiddenBatchesChange}
            onExport={onChartExport}
            onChartReady={onChartReady}
          />
        )}
      </ChartPlaceholder>
    )}

    {'loading' !== answer ? (
      isComponentOn(components, 'subQuestions') ? (
        isSummaryView ? (
          // Summary drops the interactive package-picker batches for a plain importance-% table.
          <Table
            id={String(questionId)}
            {...renderImportanceTableProps(answer, batches, persona, sortOrder)}
            onSort={(colIndex, dir, cols) => {
              const order = calcSortOrder(cols, colIndex, dir)
              if (order) onSortChange(order)
            }}
          />
        ) : (
          <>
            {isComponentOn(components, 'subQuestions') ? (
              <Items
                id={questionId}
                items={batches}
                answer={answer}
                mode={mode}
                persona={persona}
                currentPackage={currentPackage}
                sortOrder={sortOrder}
                uncollapsed={uncollapsed}
                isSummaryView={isSummaryView}
                hideTotal={hideTotal}
                processText={processText}
                onModeChange={onModeChange}
                onSortChange={onSortChange}
                onToggleCollapse={onToggleCollapse}
                onToggleAll={onToggleAll}
                onPackageChange={onBatchPackageChange}
              />
            ) : null}
            {isComponentOn(components, 'cjMarketSimulator') ? (
              <MarketSimulator
                id={questionId}
                features={features}
                answer={answer}
                batches={batches}
                mode={mode}
                persona={persona}
                marketSimulatorPackages={marketSimulatorPackages}
                marketSimulatorNA={marketSimulatorNA}
                onNAChange={onNAChange}
                onExport={onMarketSimulatorExport}
                addMarketSimulatorPackage={addMarketSimulatorPackage}
                deleteMarketSimulatorPackage={deleteMarketSimulatorPackage}
                setMarketSimulatorPackage={setMarketSimulatorPackage}
              />
            ) : null}
          </>
        )
      ) : null
    ) : null}
  </>
)

Body.displayName = 'ConjointBody'

Body.propTypes = {
  answer: answerPropType,
  persona: PropTypes.number,
  questionType: PropTypes.oneOf(['cjc', 'cjd']),
  batches: PropTypes.array,
  features: PropTypes.object,
  mode: PropTypes.string,
  hiddenBatches: PropTypes.array,
  currentPackage: PropTypes.string,
  packageRankSharesArr: PropTypes.array,
  packageRankScoresArr: PropTypes.array,
  sortOrder: PropTypes.string,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  chartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  isSummaryView: PropTypes.bool,
  isAnimated: PropTypes.bool,
  scale: PropTypes.bool,
  center: PropTypes.bool,
  components: PropTypes.object,
  uncollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  hideTotal: PropTypes.bool,
  processText: PropTypes.func,
  marketSimulatorPackages: PropTypes.array,
  marketSimulatorNA: PropTypes.bool,
  onPackageChange: PropTypes.func,
  onHiddenBatchesChange: PropTypes.func,
  onChartExport: PropTypes.func,
  onChartReady: PropTypes.func,
  onChartModeChange: PropTypes.func,
  onZoomChange: PropTypes.func,
  onModeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onToggleAll: PropTypes.func,
  onToggleCollapse: PropTypes.func,
  onBatchPackageChange: PropTypes.func,
  onNAChange: PropTypes.func,
  onMarketSimulatorExport: PropTypes.func,
  addMarketSimulatorPackage: PropTypes.func,
  deleteMarketSimulatorPackage: PropTypes.func,
  setMarketSimulatorPackage: PropTypes.func
}

export default Body
